/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { subscribe } from "utils/keyboard";
import { isInputOrEditableDiv } from "utils/DomUtils";
import { isControlORCommandPressed } from "dc-preview-utils/src/Utils/EventHandlingUtils";
import { isWindows } from "dc-preview-utils/src/Utils/DeviceUtils";
import { noop, clone } from "utils/lodashUtils";
import { trackKeyboardShortcut, trackUndoRedoCommand } from "../analytics/events";

class CommandManager {
  constructor() {
    this.reset();
  }

  _keyboardHandler(evt) {
    let activeElement = document.activeElement;
    let undoRedoHandlerCalled = false;

    if (activeElement) {
      // below change of event.target.id === "inputGsv" should be removed later
      if (isInputOrEditableDiv(activeElement) && evt.target.id !== "inputGsv") return undoRedoHandlerCalled;
    }

    if (isControlORCommandPressed(evt)) {
      if (evt.keyCode === 90) {
        if (evt.shiftKey) {
          evt.preventDefault();
          this.redo();
          trackKeyboardShortcut("Ctrl+Shift+z", "redo");
        } else {
          evt.preventDefault();
          this.undo();
          trackKeyboardShortcut("Ctrl+z", "undo");
        }
        undoRedoHandlerCalled = true;
      } else if (isWindows() && evt.keyCode === 89) {
        // Ctrl + Y as redo: only for Windows
        evt.preventDefault();
        this.redo();
        trackKeyboardShortcut("Ctrl+Shift+z", "redo");
        undoRedoHandlerCalled = true;
      }
    }
    return undoRedoHandlerCalled;
  }

  removeLastEntryFromUndoStack() {
    this.undoStack.pop();
    this.postActionHandler.forEach(fn => fn());
  }

  clearStack() {
    if (this.undoStack && this.undoStack.length > 0) {
      this.undoStackCleared = true;
    }
    this.undoStack = [];
    this.redoStack = [];
    if (this.postActionHandler) {
      this.postActionHandler.forEach(fn => fn());
    }
  }

  getUndoStackCleared() {
    return this.undoStackCleared ? this.undoStackCleared : false;
  }

  reset() {
    this.clearStack();

    this.preUndoHandler = [];
    this.preRedoHandler = [];
    this.postActionHandler = [];
    this._keyboardHandler = this._keyboardHandler.bind(this);
    subscribe(this._keyboardHandler);
  }

  executeCommand(cmd) {
    if (cmd) {
      this.redoStack = [];
      cmd.execute();
      this.undoStack.push(cmd);
      this.undoStackCleared = false;
      this.postActionHandler.forEach(fn => fn());
    }
  }

  undo(opts) {
    if (!this.undoStack.length) return;
    const currCommand = this.undoStack[this.undoStack.length - 1];
    Promise.all(this.preUndoHandler.map(fn => fn(currCommand)))
      .then(() => {
        const cmd = this.undoStack.pop();
        if (cmd) {
          cmd.undo(opts);
          this.redoStack.push(cmd);
          this.postActionHandler.forEach(fn => fn());
          trackUndoRedoCommand("undo");
        }
      })
      .catch(() => noop());
  }

  redo(opts) {
    if (!this.redoStack.length) return;
    const currCommand = this.redoStack[this.redoStack.length - 1];
    Promise.all(this.preRedoHandler.map(fn => fn(currCommand)))
      .then(() => {
        const cmd = this.redoStack.pop();
        if (cmd) {
          cmd.redo(opts);
          this.undoStack.push(cmd);
          this.undoStackCleared = false;
          this.postActionHandler.forEach(fn => fn());
          trackUndoRedoCommand("redo");
        }
      })
      .catch(() => noop());
  }

  hasUndoCommand() {
    return this.undoStack.length > 0;
  }

  hasRedoCommand() {
    return this.redoStack.length > 0;
  }

  registerPreUndoHandler(fn) {
    this.preUndoHandler.push(fn);
  }

  registerPreRedoHandler(fn) {
    this.preRedoHandler.push(fn);
  }

  registerPostActionHandler(fn) {
    this.postActionHandler.push(fn);
  }

  getUndoStack() {
    return this.undoStack;
  }

  cloneUndoStack() {
    return clone(this.undoStack);
  }
}

export default new CommandManager();
